import React, { createContext, useState, useContext, useCallback } from 'react';
import { X } from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'info', durationMs = 5000) => {
        const id = Date.now() + Math.random(); // Unique ID for each toast
        const newToast = {
            id,
            message,
            type, // 'success', 'error', 'warning', 'info'
        };

        setToasts((prevToasts) => [...prevToasts, newToast]);

        const ttl = typeof durationMs === 'number' && durationMs > 0 ? durationMs : 5000;
        setTimeout(() => {
            setToasts((prevToasts) => prevToasts.filter(toast => toast.id !== id));
        }, ttl);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts((prevToasts) => prevToasts.filter(toast => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast, removeToast }}>
            {children}
            {/* Toast Container */}
            <div className="fixed top-4 right-4 z-50 space-y-2">
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        className={`flex items-start gap-2 p-4 rounded-lg shadow-lg transform transition-all duration-300 ${
                            toast.type === 'warning' ? 'max-w-lg' : 'max-w-md'
                        } ${
                            toast.type === 'success' ? 'bg-green-500 text-white' :
                            toast.type === 'error' ? 'bg-red-500 text-white' :
                            toast.type === 'warning' ? 'bg-amber-100 text-amber-950 border border-amber-300' :
                            'bg-primary-500 text-white'
                        }`}
                    >
                        <span className="mr-2">{toast.message}</span>
                        <button
                            onClick={() => removeToast(toast.id)}
                            className="ml-auto text-current hover:opacity-75"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};